import {
  closestCenter,
  DndContext,
  type DragEndEvent,
  DragOverlay,
  type DragStartEvent,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import { arrayMove, rectSortingStrategy, SortableContext, useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { type KeyboardEvent, type ReactNode, useEffect, useState } from "react";
import { cx } from "@/utils/cx";
import type { MetricDefinition, MetricKey, MetricReading } from "../lib/metrics";
import { isReorderKey, movePinByKey } from "../lib/pins";
import { CustomisePopover } from "./customise-popover";
import { ChangePill, Sparkline } from "./parts";

export type MetricTile = { metric: MetricDefinition; reading: MetricReading; series: number[] };

type MetricTilesProps = {
  tiles: MetricTile[];
  comparisonTitle: string;
  pins: MetricKey[];
  onPinsChange: (pins: MetricKey[]) => void;
  onReset: () => void;
  currency: string;
  currencyPicker: ReactNode;
};

const MAX_COLUMNS = 5;
const DRAG_THRESHOLD_PX = 3;
const SLIDE = { duration: 220, easing: "cubic-bezier(.2,.8,.2,1)" };
const GRIP_DOTS = [
  [4, 2.5],
  [8, 2.5],
  [4, 6],
  [8, 6],
  [4, 9.5],
  [8, 9.5],
];

const Grip = () => (
  <svg viewBox="0 0 12 12" width={12} height={12} aria-hidden="true" fill="currentColor">
    {GRIP_DOTS.map(([x, y]) => (
      <circle key={`${x}-${y}`} cx={x} cy={y} r={1.25} />
    ))}
  </svg>
);

type TileBodyProps = { tile: MetricTile; comparisonTitle: string; currency: string };

const TileBody = ({ tile, comparisonTitle, currency }: TileBodyProps) => (
  <>
    <span className="truncate pr-6 text-[13px] text-[#21133a]/75">{tile.metric.label}</span>
    <div className="flex items-center gap-2 whitespace-nowrap">
      <span className="text-[22px] leading-8 font-semibold text-[#21133a] tabular-nums">
        {tile.reading.value === null ? "—" : tile.metric.format(tile.reading.value, currency)}
      </span>
      <ChangePill change={tile.reading.change} unit={tile.metric.change} title={comparisonTitle} />
    </div>
    <Sparkline points={tile.series} />
  </>
);

const TILE_CLASS = "flex flex-col gap-2 rounded-xl border border-[#21133a]/8 bg-unbox-cream-25 p-5";

type SortableTileProps = {
  tile: MetricTile;
  comparisonTitle: string;
  currency: string;
  onKeyMove: (key: MetricKey, event: KeyboardEvent<HTMLButtonElement>) => void;
};

const SortableTile = ({ tile, comparisonTitle, currency, onKeyMove }: SortableTileProps) => {
  const { setNodeRef, setActivatorNodeRef, listeners, transform, transition, isDragging } = useSortable({
    id: tile.metric.key,
    transition: SLIDE,
  });

  return (
    <div
      ref={setNodeRef}
      style={{ transform: CSS.Transform.toString(transform), transition }}
      className={cx("group relative", TILE_CLASS, isDragging && "opacity-0")}
    >
      <button
        ref={setActivatorNodeRef}
        type="button"
        data-grip={tile.metric.key}
        aria-label={`Move ${tile.metric.label}`}
        title="Drag to reorder"
        {...listeners}
        onKeyDown={(event) => onKeyMove(tile.metric.key, event)}
        className="absolute top-3 right-3 flex size-6 cursor-grab touch-none items-center justify-center rounded-md text-[#21133a]/50 opacity-0 transition-opacity group-hover:opacity-100 hover:bg-[#21133a]/5 focus-visible:opacity-100"
      >
        <Grip />
      </button>
      <TileBody tile={tile} comparisonTitle={comparisonTitle} currency={currency} />
    </div>
  );
};

const setDragging = (dragging: boolean) => {
  document.body.style.cursor = dragging ? "grabbing" : "";
  document.body.style.userSelect = dragging ? "none" : "";
};

export function MetricTiles({
  tiles,
  comparisonTitle,
  pins,
  onPinsChange,
  onReset,
  currency,
  currencyPicker,
}: MetricTilesProps) {
  const [activeKey, setActiveKey] = useState<MetricKey | null>(null);
  const [focusKey, setFocusKey] = useState<MetricKey | null>(null);
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: DRAG_THRESHOLD_PX } }));
  const columns = Math.min(MAX_COLUMNS, Math.max(1, tiles.length));
  const activeTile = tiles.find((tile) => tile.metric.key === activeKey);

  useEffect(() => {
    if (!focusKey) return;
    document.querySelector<HTMLButtonElement>(`[data-grip="${focusKey}"]`)?.focus();
    setFocusKey(null);
  }, [focusKey]);

  useEffect(() => () => setDragging(false), []);

  const endDrag = () => {
    setActiveKey(null);
    setDragging(false);
  };

  const onDragStart = (event: DragStartEvent) => {
    setActiveKey(event.active.id as MetricKey);
    setDragging(true);
  };

  const onDragEnd = ({ active, over }: DragEndEvent) => {
    endDrag();
    if (!over || active.id === over.id) return;
    onPinsChange(arrayMove(pins, pins.indexOf(active.id as MetricKey), pins.indexOf(over.id as MetricKey)));
  };

  const onKeyMove = (key: MetricKey, event: KeyboardEvent<HTMLButtonElement>) => {
    if (!isReorderKey(event.key)) return;
    event.preventDefault();
    const next = movePinByKey(pins, key, event.key, columns);
    if (next === pins) return;
    onPinsChange(next);
    setFocusKey(key);
  };

  return (
    <section aria-label="Pinned metrics" className="flex flex-col gap-3">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <span className="text-sm text-[#21133a]/70">Metrics</span>
          <span aria-hidden="true" className="text-sm text-[#21133a]/40">
            ·
          </span>
          {currencyPicker}
        </div>
        <CustomisePopover pins={pins} onChange={onPinsChange} onReset={onReset} />
      </div>
      {tiles.length === 0 ? (
        <p className="rounded-xl border border-dashed border-[#21133a]/15 px-5 py-8 text-center text-sm text-[#21133a]/60">
          No metrics pinned. Choose Customise to add some.
        </p>
      ) : (
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          autoScroll={false}
          onDragStart={onDragStart}
          onDragEnd={onDragEnd}
          onDragCancel={endDrag}
        >
          <SortableContext items={pins} strategy={rectSortingStrategy}>
            <div className="grid gap-4" style={{ gridTemplateColumns: `repeat(${columns}, minmax(0, 1fr))` }}>
              {tiles.map((tile) => (
                <SortableTile
                  key={tile.metric.key}
                  tile={tile}
                  comparisonTitle={comparisonTitle}
                  currency={currency}
                  onKeyMove={onKeyMove}
                />
              ))}
            </div>
          </SortableContext>
          <DragOverlay dropAnimation={SLIDE}>
            {activeTile && (
              <div className={cx(TILE_CLASS, "scale-[1.03] shadow-[0_12px_32px_-8px_rgba(33,19,58,0.25)]")}>
                <TileBody tile={activeTile} comparisonTitle={comparisonTitle} currency={currency} />
              </div>
            )}
          </DragOverlay>
        </DndContext>
      )}
    </section>
  );
}
